// BatSeqDetailEngine.js
//
// FE Engine Pattern V2 (rk=beeca47db94541d594bc4aa830f62672) — initial fetch + SignalR
// OnBatSeqDetailsUpdated (array of changed details only) → addOrUpdate per rk.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import batSeqService from '@/services/BatSeqService';
import responseHelper from '../../../services/ResponseHelper';
import batSeqDetailCache from './BatSeqDetailCache';

const _name = 'BatSeqDetailEngine';

class BatSeqDetailEngine extends WssEventHandlerBase {
    _name = _name;

    bootup() {
        super.bootup();
        this.clear();
        this._unsubMessage = this._wsNotifier.subscribe(events.OnBatSeqDetailsUpdated, this.onMessage);
        this._unsubRemoved = this._wsNotifier.subscribe(events.OnBatSeqDetailsDeleted, this.onRemoved);
    }

    clear() {
        super.clear();
        if (this._unsubMessage) this._unsubMessage();
        if (this._unsubRemoved) this._unsubRemoved();
    }

    /**
     * BE push — changed details only.
     * @param {object[]} items
     */
    onMessage = (items) => {
        try {
            batSeqDetailCache.addOrUpdate(items ?? []);
            this._notify();
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * BE push — removed details.
     * @param {object[]} items
     */
    onRemoved = (items) => {
        try {
            items.forEach((x) => {
                batSeqDetailCache.delete(x.rk);
            });
            this._notify();
        } catch (error) {
            this.logError('onRemoved', error.message);
        }
    };

    /**
     * Initial fetch — all details via REST.
     * @returns {Promise<object[]>}
     */
    fetch = async () => {
        try {
            const res = await batSeqService.getDetails();
            const items = res?.data?.items ?? [];
            batSeqDetailCache.setCache([...items]);
            this._notify();
            return items;
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * Adds or updates a detail, cache follows via SignalR push.
     * @param {object} data - BatSeqDetail DTO
     * @returns {Promise<boolean>} success
     */
    add = (data) => this.post([data]);

    /**
     * Adds or updates details in 1 call.
     * @param {object[]} items - BatSeqDetail DTOs
     * @returns {Promise<boolean>} success
     */
    post = async (items) => {
        try {
            const resp = await batSeqService.postDetails(items);
            return responseHelper.check(resp, '', _name) >= 0;
        } catch (error) {
            this.logError('post', error.message);
            return false;
        }
    };

    /**
     * Deletes a detail, full DTO in body.
     * @param {object} detail - BatSeqDetail DTO
     * @returns {Promise<boolean>} success
     */
    remove = async (detail) => {
        try {
            const resp = await batSeqService.deleteDetails([detail]);
            if (responseHelper.check(resp, '', _name) < 0) return false;
            batSeqDetailCache.delete(detail.rk);
            this._notify();
            return true;
        } catch (error) {
            this.logError('remove', error.message);
            return false;
        }
    };

    /**
     * Moves a step up or down, renumbers order = index and posts only changed steps in 1 call.
     * @param {object} detail - BatSeqDetail DTO
     * @param {number} dir - BAT_SEQ_MOVE.Up (-1) or Down (1)
     * @returns {Promise<boolean>} success
     */
    move = async (detail, dir) => {
        const steps = this.getByRef(detail.ref);
        const idx = steps.findIndex((d) => d.rk === detail.rk);
        const target = idx + dir;
        if (target < 0 || target >= steps.length) return false;

        steps.splice(idx, 1);
        steps.splice(target, 0, detail);
        const changed = steps.map((d, i) => ({ ...d, order: i })).filter((d, i) => steps[i].order !== d.order);

        return this.post(changed);
    };

    /**
     * @param {string} ref - master rk
     * @returns {object[]} steps sorted by order
     */
    getByRef = (ref) => batSeqDetailCache.byRef(ref);

    /**
     * @param {string} ref - master rk
     * @returns {number} order for a new last step
     */
    nextOrder = (ref) => this.getByRef(ref).length;

    _notify = () => {
        ComponentNotifier.notify(_name, 'dataChanged', {});
    };
}

const batSeqDetailEngine = new BatSeqDetailEngine();
export default batSeqDetailEngine;
